import { useState, useEffect, useCallback } from 'react';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import { getAuditTrail, clearAuditTrail } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { History, Filter, Trash2 } from 'lucide-react';
import { ExportToolbar } from '../components/ui/export-toolbar';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { toast } from 'sonner';

const AuditTrail = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAuditTrail(entityFilter === 'all' ? undefined : entityFilter);
      setLogs(res.data);
    } catch (error) {
      toast.error('Failed to fetch audit trail');
    } finally {
      setLoading(false);
    }
  }, [entityFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const { sortedData: sortedLogs, sortKey: auditSortKey, sortDirection: auditSortDir, handleSort: handleAuditSort } = useSortableTable(logs, 'created_at', 'desc');

  const getActionBadge = (action) => {
    const variants = {
      create: 'default',
      update: 'secondary',
      delete: 'destructive',
      approve: 'outline',
      payment: 'outline',
      password_change: 'secondary',
      password_reset: 'secondary'
    };
    return <Badge variant={variants[action] || 'secondary'}>{action}</Badge>;
  };

  const getEntityBadge = (entityType) => {
    const colors = {
      income: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
      expense: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
      sale: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
      customer: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100',
      bank_transfer: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100',
      user_transfer: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100',
      recurring: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-100',
      user: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[entityType] || 'bg-gray-100 text-gray-800'}`}>
        {entityType}
      </span>
    );
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all audit logs? This cannot be undone.')) return;
    try {
      const res = await clearAuditTrail();
      toast.success(res.data.message);
      setLogs([]);
    } catch (err) {
      toast.error('Failed to clear audit trail');
    }
  };

  usePageCallbacks({
    onClearAction: handleClearLogs,
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Audit Trail</h1>
          <p className="text-muted-foreground">Track all system activities</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-[180px]" data-testid="audit-filter-select">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="income">Income</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
              <SelectItem value="sale">Sales</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="bank_transfer">Bank Transfers</SelectItem>
              <SelectItem value="user_transfer">User Transfers</SelectItem>
              <SelectItem value="recurring">Recurring</SelectItem>
              <SelectItem value="user">Users</SelectItem>
            </SelectContent>
          </Select>
          <ExportToolbar endpoint="/exports/audit-trail" fileBaseName="audit-trail" params={{ entity_type: entityFilter }} testId="export-audit" />
          <Button variant="destructive" size="sm" onClick={handleClearLogs} data-testid="clear-audit-btn">
            <Trash2 className="h-4 w-4 mr-1" />Clear Logs
          </Button>
        </div>
      </div>

      {/* Audit Log Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5" />
            Activity Log
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortableHeader label="Timestamp" sortKey="created_at" currentSortKey={auditSortKey} sortDirection={auditSortDir} onSort={handleAuditSort} /></TableHead>
                  <TableHead><SortableHeader label="User" sortKey="username" currentSortKey={auditSortKey} sortDirection={auditSortDir} onSort={handleAuditSort} /></TableHead>
                  <TableHead><SortableHeader label="Action" sortKey="action" currentSortKey={auditSortKey} sortDirection={auditSortDir} onSort={handleAuditSort} /></TableHead>
                  <TableHead>Entity Type</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedLogs.map(log => (
                    <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDateTime(log.timestamp)}
                      </TableCell>
                      <TableCell className="font-medium">{log.user_name || 'System'}</TableCell>
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell>{getEntityBadge(log.entity_type)}</TableCell>
                      <TableCell className="max-w-[300px]">
                        <div className="text-sm text-muted-foreground">
                          {Object.entries(log.details || {}).map(([key, value]) => (
                            <span key={key} className="mr-2">
                              <span className="font-medium">{key}:</span> {String(value)}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditTrail;
